// Serviço do Mercado Pago integrado com Supabase
// Usa as novas APIs para controle de duplicação

export interface PaymentData {
  userId: string
  tokenAmount: number
  userEmail: string
  userName: string
  reaisAmount: number
}

export interface PaymentResponse {
  preference_id: string
  init_point: string
  sandbox_init_point: string
  purchase_id: string
  token_amount: number
  reais_amount: number
}

// Função para obter a URL correta baseada no ambiente
const getCheckoutUrl = () => {
  // Em desenvolvimento, usar o proxy do Vite
  if (import.meta.env.DEV || window.location.hostname === 'localhost') {
    return '/api/tokens/checkout'
  }
  // Em produção, chamar diretamente a Cloud Function
  return 'https://us-central1-overpixel-hub.cloudfunctions.net/createTokensCheckout'
}

const getVerifyUrl = () => {
  // Em desenvolvimento, usar o proxy do Vite
  if (import.meta.env.DEV || window.location.hostname === 'localhost') {
    return '/api/tokens/verify'
  }
  // Em produção, chamar diretamente a Cloud Function
  return 'https://us-central1-overpixel-hub.cloudfunctions.net/verifyTokensPayment'
}

// Criar preferência de pagamento usando a nova API
export const createPaymentPreference = async (paymentData: PaymentData): Promise<PaymentResponse> => {
  try {
    console.log('🔄 Enviando dados para checkout:', paymentData)
    
    const checkoutUrl = getCheckoutUrl()
    console.log('🌐 URL do checkout:', checkoutUrl)
    
    const response = await fetch(checkoutUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(paymentData)
    })

    console.log('📡 Resposta do servidor:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    })

    if (!response.ok) {
      const responseText = await response.text()
      console.error('❌ Resposta de erro:', responseText)
      
      // Tentar fazer parse do JSON se possível
      try {
        const errorData = JSON.parse(responseText)
        throw new Error(errorData.error || 'Erro ao processar pagamento')
      } catch {
        throw new Error(`Erro do servidor (${response.status}): ${responseText.substring(0, 200)}...`)
      }
    }

    const responseText = await response.text()
    console.log('📄 Resposta completa:', responseText)
    
    let result
    try {
      result = JSON.parse(responseText)
    } catch (parseError) {
      console.error('❌ Erro ao fazer parse do JSON:', parseError)
      console.error('📄 Conteúdo recebido:', responseText.substring(0, 500))
      throw new Error('Resposta inválida do servidor. Verifique se o proxy está configurado corretamente.')
    }
    
    if (!result.success) {
      throw new Error(result.error || 'Erro ao processar pagamento')
    }

    return {
      preference_id: result.data.preference_id,
      init_point: result.data.init_point,
      sandbox_init_point: result.data.sandbox_init_point,
      purchase_id: result.data.purchase_id,
      token_amount: result.data.token_amount,
      reais_amount: result.data.reais_amount
    }
  } catch (error) {
    console.error('❌ Erro ao criar preferência de pagamento:', error)
    throw error
  }
}

// Verificar status do pagamento usando a nova API
export const checkPaymentStatus = async (paymentId: string) => {
  try {
    const verifyUrl = getVerifyUrl()
    console.log('🔍 URL de verificação:', verifyUrl)
    
    const response = await fetch(`${verifyUrl}?payment_id=${paymentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Erro ao verificar status do pagamento')
    }

    const result = await response.json()
    return result
  } catch (error) {
    console.error('Erro ao verificar status do pagamento:', error)
    throw error
  }
}

// Processar webhook do Mercado Pago (agora handled pela API)
export const processWebhook = async (webhookData: any) => {
  // Esta função não é mais necessária pois o webhook é processado
  // diretamente pela API /api/webhooks/mercadopago
  console.log('Webhook será processado pela API:', webhookData)
  return { success: true, message: 'Webhook será processado pela API' }
}

// Redirecionar para pagamento
export const redirectToPayment = (paymentResponse: PaymentResponse) => {
  const isProduction = import.meta.env.PROD
  const paymentUrl = isProduction ? paymentResponse.init_point : paymentResponse.sandbox_init_point
  
  // Abrir em nova aba
  window.open(paymentUrl, '_blank')
}

// Validar configuração do Mercado Pago
export const validateMercadoPagoConfig = (): boolean => {
  const accessToken = import.meta.env.VITE_MERCADO_PAGO_ACCESS_TOKEN
  return !!accessToken && accessToken.length > 0
}

// --- Assinaturas Mercado Pago --- //
// Checkout hospedado de assinatura baseado em preapproval_plan
export const createSubscriptionPlanCheckout = async (
  planKey: 'explorador' | 'criador' | 'empresarial_pro',
  backUrl?: string
): Promise<{ init_point: string; plan_id: string; title: string; amount: number }> => {
  try {
    const endpoint = (import.meta.env.DEV || window.location.hostname === 'localhost')
      ? '/api/subscriptions/checkout'
      : 'https://us-central1-overpixel-hub.cloudfunctions.net/getSubscriptionPlanCheckout'

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planKey, backUrl })
    })

    const resultText = await response.text()
    let result: any
    try {
      result = JSON.parse(resultText)
    } catch (e) {
      console.error('Erro ao parsear resposta de assinatura:', resultText)
      throw new Error('Resposta inválida do servidor de assinaturas')
    }

    if (!response.ok || !result.success || !result.init_point) {
      const message = result?.error || 'Falha ao criar checkout de assinatura'
      console.error('Assinatura não criada:', result)
      throw new Error(message)
    }

    return {
      init_point: result.init_point,
      plan_id: result.plan_id,
      title: result.title,
      amount: result.amount
    }
  } catch (error) {
    console.error('❌ Erro ao criar checkout de assinatura:', error)
    throw error
  }
}

// Novo: Checkout de assinatura que grava mapeamento do usuário (userEmail/userId) no backend
export const createSubscriptionCheckout = async (
  planKey: 'explorador' | 'criador' | 'empresarial_pro',
  backUrl: string | undefined,
  userEmail: string | null | undefined,
): Promise<{ init_point: string; plan_id: string; title: string; amount: number }> => {
  try {
    const isDev = (import.meta.env.DEV || window.location.hostname === 'localhost')
    const checkoutEndpoint = isDev
      ? '/api/subscriptions/checkout'
      : 'https://us-central1-overpixel-hub.cloudfunctions.net/getSubscriptionPlanCheckout'
    const createEndpoint = isDev
      ? '/api/subscriptions/create'
      : 'https://us-central1-overpixel-hub.cloudfunctions.net/createSubscription'

    // 1) Criar/obter o plano de assinatura com preços corretos e init_point
    const planResp = await fetch(checkoutEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planKey, backUrl })
    })
    const planText = await planResp.text()
    let planResult: any
    try { planResult = JSON.parse(planText) } catch (e) {
      console.error('Erro ao parsear resposta de assinatura (checkout):', planText)
      throw new Error('Resposta inválida do servidor de assinaturas')
    }
    if (!planResp.ok || !planResult?.success || !planResult?.plan_id || !planResult?.init_point) {
      const message = planResult?.error || 'Falha ao criar checkout de assinatura'
      console.error('Assinatura não criada (checkout):', planResult)
      throw new Error(message)
    }

    const planId = planResult.plan_id

    // 2) Gravar mapeamento do usuário no backend usando preapproval_plan_id
    const mapResp = await fetch(createEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preapproval_plan_id: planId, userEmail, planKey })
    })
    const mapText = await mapResp.text()
    let mapResult: any = {}
    try { mapResult = JSON.parse(mapText) } catch (e) {
      console.warn('Aviso: resposta não JSON em createSubscription. Usando init_point do checkout.', mapText)
    }
    if (!mapResp.ok) {
      console.error('Falha ao gravar mapeamento no backend:', mapResult)
    }

    // Preferir init_point do createSubscription se disponível; caso contrário, usar do checkout
    const initPoint = mapResult?.init_point || planResult.init_point

    return {
      init_point: initPoint,
      plan_id: planId,
      title: planResult.title,
      amount: planResult.amount
    }
  } catch (error) {
    console.error('❌ Erro ao criar checkout de assinatura (com mapeamento):', error)
    throw error
  }
}

export default {
  createPaymentPreference,
  checkPaymentStatus,
  processWebhook,
  redirectToPayment,
  validateMercadoPagoConfig,
  createSubscriptionPlanCheckout,
  createSubscriptionCheckout
}